import { type TextEditorValue } from "@/common/TextEditor";
import { type EXEC_TYPE, type FORM_TYPE } from "@/constants/global";
import { type PREPROCESSING_WF_PAGE_PROCESS_TYPE, type PREPROCESSING_WF_UPLOAD_MODE } from "@/constants/workflow";
import { type ServerGroupBy } from "@/pages/Workflow/WorkflowUpsert/reducer/interface";

import { type SelectOption } from "./global";
import {
  type DetectableClass,
  type DetectableClassServer,
  type PipelineBasicInfo,
  type PipelineVersion,
  type Target,
} from "./pipeline";
import { type TeamInShort } from "./team";
import { type UserInShort } from "./user";

/*
 * ============================================================================
 * Base Types & Enums
 * ============================================================================
 */

export type WorkflowId = string;

export enum CSV_OUTPUT_TYPE {
  SHIFT_JIS = "shift-jis",
  UTF_8 = "utf-8",
  UTF_8_BOM = "utf-8-sig",
}

export enum FilenameConfigurationType {
  PLACEHOLDER = "placeholder",
  LITERAL = "literal",
  UPLOADED_DATE = "uploaded-date",
}

/*
 * ============================================================================
 * CSV Output Configuration Types
 * ============================================================================
 */
export interface CSVOutputConfig {
  csv_export_encoding: CSV_OUTPUT_TYPE;
  csv_exclude_filename: boolean;
}

/*
 * ============================================================================
 * Filename Configuration Types
 * ============================================================================
 */

export type FilenameConfigType = string | SelectOption;

interface FileNameWorkflowConfigurationBase {
  enabled_filename_config: boolean;
}

export interface FileNameWorkflowConfigurationServer extends FileNameWorkflowConfigurationBase {
  filename_config: FilenameConfigServer[];
}

export interface FileNameWorkflowConfiguration extends FileNameWorkflowConfigurationBase {
  filename_config: FilenameConfigType[];
}

export interface FilenameConfig {
  enabled: boolean;
  content: TextEditorValue[];
}

type FilenameConfigValueUploadedDate = {
  type: FilenameConfigurationType.UPLOADED_DATE;
  value: null;
};

type FilenameConfigValuePlaceholder = {
  type: FilenameConfigurationType.PLACEHOLDER;
  value: string;
};

type FilenameConfigValueLiteral = {
  type: FilenameConfigurationType.LITERAL;
  value: string;
};

export type FilenameConfigServer =
  FilenameConfigValueUploadedDate | FilenameConfigValuePlaceholder | FilenameConfigValueLiteral;

/*
 * ============================================================================
 * Preprocessing Workflow Types
 * ============================================================================
 */

export type PreprocessingPageProcess =
  | {
      type: PREPROCESSING_WF_PAGE_PROCESS_TYPE.ALL;
      value?: null;
    }
  | {
      type: PREPROCESSING_WF_PAGE_PROCESS_TYPE.SPECIFIC_PAGE | PREPROCESSING_WF_PAGE_PROCESS_TYPE.SPECIFIC_RANGE;
      value: string[];
    };

export interface PreprocessingWorkflow {
  uploadMode: PREPROCESSING_WF_UPLOAD_MODE;
  rotation: boolean;
  processingPages: PreprocessingPageProcess;
  pagesPerFile: string;
}

export interface PreprocessingWorkflowServer extends Omit<
  PreprocessingWorkflow,
  "processingPages" | "uploadMode" | "pagesPerFile"
> {
  upload_mode?: PREPROCESSING_WF_UPLOAD_MODE;
  processing_pages: string;
  split_pages_per_job?: number | null;
}

/*
 * ============================================================================
 * Post Processors Types
 * ============================================================================
 */

export interface PostProcessorsClassification {
  group_by: ServerGroupBy;
  requires_approval: boolean;
  ocr: Record<string, string | null>;
}

export type PostProcessors = Omit<PostProcessorsClassification, "group_by" | "ocr">;

/*
 * ============================================================================
 * Executor Types
 * ============================================================================
 */

/**
 * Base executor interface representing the full executor structure
 * used in the client application.
 */
export interface Executor extends Partial<FileNameWorkflowConfiguration> {
  pipeline: Pick<PipelineBasicInfo, "id" | "display_name" | "tag" | "version">;
  pipeline_version?: PipelineVersion;
  targets?: Record<string, Target | null>;
  detectable_classes?: DetectableClass[];
  version: number;
  pre_processors: PreprocessingWorkflow;
  post_processors: PostProcessors | null;
  post_processors_classification: PostProcessorsClassification | null;
  csv_export_encoding?: CSV_OUTPUT_TYPE;
  csv_exclude_filename?: boolean;
}

/**
 * Executor payload for creating/updating workflows.
 * Converts pipeline to string ID and makes post_processors partial.
 */
export interface ExecutorPayload
  extends
    Omit<
      Executor,
      | "pipeline"
      | "version"
      | "post_processors"
      | "post_processors_classification"
      | "filename_config"
      | "enabled_filename_config"
    >,
    Partial<FileNameWorkflowConfiguration> {
  pipeline?: string;
  post_processors: Partial<PostProcessors> | null;
  post_processors_classification: Partial<PostProcessorsClassification> | null;
}

/**
 * Server-side executor payload format.
 * Uses server-specific types for pre_processors and detectable_classes.
 */
export interface ExecutorPayloadServer
  extends
    Omit<ExecutorPayload, "pre_processors" | "detectable_classes" | "filename_config" | "enabled_filename_config">,
    Partial<FileNameWorkflowConfigurationServer> {
  pre_processors: PreprocessingWorkflowServer;
  detectable_classes?: DetectableClassServer[];
}

/**
 * Server-side executor format.
 * Extends Executor with server-specific preprocessing workflow.
 */
export interface ExecutorServer
  extends
    Omit<Executor, "pre_processors" | "filename_config" | "enabled_filename_config">,
    Partial<FileNameWorkflowConfigurationServer> {
  pre_processors: PreprocessingWorkflowServer;
}

/*
 * ============================================================================
 * Workflow Types
 * ============================================================================
 */

/**
 * Complete workflow structure as returned from the server.
 */
export interface WorkflowServer extends Omit<Workflow, "executor"> {
  executor: ExecutorServer;
}

/**
 * Complete workflow structure used in the client application.
 */
export interface Workflow {
  id: WorkflowId;
  form_type?: FORM_TYPE;
  executor: Executor;
  description: string;
  display_name: string;
  exec_type: EXEC_TYPE;
  is_public: boolean;
  users: UserInShort[];
  teams: TeamInShort[];
  created_at: string;
  created_by: UserInShort;
  updated_at: string;
  updated_by: UserInShort | null;
  deleted?: boolean;
}

/**
 * Workflow metadata fields that are excluded from payloads.
 */
type WorkflowMetadataFields =
  "users" | "teams" | "executor" | "id" | "created_at" | "created_by" | "updated_at" | "updated_by";

/**
 * Workflow payload for creating/updating workflows.
 * Converts users/teams to IDs and allows partial executor.
 */
export interface WorkflowPayload<T extends Partial<ExecutorPayload> | ExecutorPayload = ExecutorPayload> extends Omit<
  Workflow,
  WorkflowMetadataFields
> {
  id?: WorkflowId;
  users: (string | number)[];
  teams: (string | number)[];
  executor: T;
}

/**
 * Server-side workflow payload format.
 * Uses server-specific executor payload.
 */
export interface WorkflowPayloadServer extends Omit<WorkflowPayload, "executor"> {
  executor: Partial<ExecutorPayloadServer>;
}

/**
 * Shortened workflow structure for lists and references.
 */
export interface WorkflowInShort {
  id: WorkflowId;
  code_name: string | null;
  description: string;
  display_name: string;
}
